(() => {
  const script = document.currentScript;
  const config = {
    productFormSrc: script?.dataset.productFormSrc || '',
    productInfoSrc: script?.dataset.productInfoSrc || '',
    quickAddSrc: script?.dataset.quickAddSrc || '',
  };

  if (window.CBHQuickAddRuntimeLoader?.register) {
    window.CBHQuickAddRuntimeLoader.register(config);
    return;
  }

  const sources = { ...config };
  const scriptPromises = new Map();

  const register = (next = {}) => {
    Object.entries(next).forEach(([key, value]) => {
      if (value && !sources[key]) sources[key] = value;
    });
  };

  const absoluteUrl = (src) => {
    try {
      return new URL(src, window.location.href).href;
    } catch (error) {
      return src;
    }
  };

  const loadScript = (src) => {
    if (!src) return Promise.reject(new Error('Quick Add runtime asset URL is missing.'));

    const url = absoluteUrl(src);
    if (scriptPromises.has(url)) return scriptPromises.get(url);

    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts).find((node) => node.src === url);
      if (existing?.dataset.cbhRuntimeLoaded === 'true') {
        resolve();
        return;
      }

      const node = existing || document.createElement('script');
      const onLoad = () => {
        node.dataset.cbhRuntimeLoaded = 'true';
        resolve();
      };
      const onError = () => reject(new Error(`Failed to load ${url}`));

      node.addEventListener('load', onLoad, { once: true });
      node.addEventListener('error', onError, { once: true });

      if (!existing) {
        node.src = url;
        node.async = true;
        node.dataset.cbhRuntimeInjected = 'true';
        document.head.appendChild(node);
      }
    });

    scriptPromises.set(url, promise);
    return promise;
  };

  const directReady = () => Boolean(customElements.get('product-form'));
  const optionsReady = () =>
    directReady() && Boolean(customElements.get('product-info')) && Boolean(customElements.get('quick-add-modal'));

  let directPromise = null;
  let optionsPromise = null;

  const loadDirect = () => {
    if (directReady()) return Promise.resolve();
    directPromise ??= loadScript(sources.productFormSrc).then(() => {
      if (!directReady()) throw new Error('Product form runtime did not initialise.');
    });
    return directPromise;
  };

  const loadOptions = () => {
    if (optionsReady()) return Promise.resolve();
    optionsPromise ??= Promise.all([
      loadDirect(),
      customElements.get('product-info') ? Promise.resolve() : loadScript(sources.productInfoSrc),
      customElements.get('quick-add-modal') ? Promise.resolve() : loadScript(sources.quickAddSrc),
    ]).then(() => {
      if (!optionsReady()) throw new Error('Quick Add option runtime did not initialise.');
    });
    return optionsPromise;
  };

  const getOptionTrigger = (target) => target?.closest?.('modal-opener [data-cbh-card-atc]') || null;
  const getDirectTrigger = (target) =>
    target?.closest?.('product-form[data-cbh-direct-add] [data-cbh-card-atc]') || null;

  const warmFromTarget = (target) => {
    if (getOptionTrigger(target)) {
      loadOptions().catch(() => {});
      return;
    }
    if (getDirectTrigger(target)) loadDirect().catch(() => {});
  };

  document.addEventListener('pointerover', (event) => warmFromTarget(event.target), { passive: true });
  document.addEventListener('focusin', (event) => warmFromTarget(event.target));
  document.addEventListener('touchstart', (event) => warmFromTarget(event.target), { passive: true });

  document.addEventListener(
    'click',
    (event) => {
      const button = getOptionTrigger(event.target);
      if (!button || optionsReady()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      loadOptions()
        .then(() => button.click())
        .catch((error) => {
          window.cbhLogError?.('Quick Add runtime failed:', error);
          const productUrl = button.getAttribute('data-product-url');
          if (productUrl) window.location.assign(productUrl);
        });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target?.closest?.('product-form[data-cbh-direct-add] form');
      if (!form || directReady()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter;

      loadDirect()
        .then(() => {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
        })
        .catch((error) => {
          window.cbhLogError?.('Add to basket runtime failed:', error);
          HTMLFormElement.prototype.submit.call(form);
        });
    },
    true
  );

  window.CBHQuickAddRuntimeLoader = {
    register,
    loadDirect,
    loadOptions,
  };
})();
